import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamPermissionsContext = createContext(null);

export const TeamPermissionsProvider = ({ children }) => {
  const [teamAccess, setTeamAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamAccess();
  }, []);

  const fetchTeamAccess = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/team/my-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTeamAccess(response.data);
    } catch (error) {
      console.error('Error fetching team access:', error);
      setTeamAccess({ is_team_member: false, permissions: null });
    } finally {
      setLoading(false);
    }
  };

  // Check if user can view a specific tab
  const canView = (tab) => {
    // Not a team member = owner, can do everything
    if (!teamAccess?.is_team_member) return true;
    
    const permissions = teamAccess?.permissions || {};
    return permissions[tab] === true;
  };

  // Check if user can edit in a specific tab
  const canEdit = (tab) => {
    // Not a team member = owner, can do everything
    if (!teamAccess?.is_team_member) return true;
    
    const permissions = teamAccess?.permissions || {};
    // Must have access to the tab AND edit permission
    return permissions[tab] === true && permissions[`${tab}_edit`] === true;
  };

  // Helper to check if user is owner (not a team member)
  const isOwner = () => {
    return !teamAccess?.is_team_member;
  };

  return (
    <TeamPermissionsContext.Provider value={{ 
      teamAccess, 
      loading, 
      canView, 
      canEdit, 
      isOwner,
      refetch: fetchTeamAccess 
    }}>
      {children}
    </TeamPermissionsContext.Provider>
  );
};

export const useTeamPermissions = () => {
  const context = useContext(TeamPermissionsContext);
  if (!context) {
    throw new Error('useTeamPermissions must be used within a TeamPermissionsProvider');
  }
  return context;
};

export default TeamPermissionsContext;
